import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface KnowledgeItem {
  id: number;
  category: string;
  document_name: string;
  content: string;
  question_answers: any[];
  priority: number;
  uploaded_at: string;
  total_chunks?: number;
  is_chunked?: boolean;
}

export interface UploadResponse {
  id: number;
  message: string;
  extractedQAs: number;
  totalChunks: number;
  skippedChunks: number;
}

export interface SearchResult {
  text: string;
  similarity: number;
  document: string;
  category: string;
}

export interface SearchTestResponse {
  query: string;
  results: SearchResult[];
  total: number;
}

export interface KnowledgeStats {
  totalChunks: number;
  chunksWithEmbeddings: number;
  averageChunkSize: number;
  categories: string[];
}

class KnowledgeService {
  /**
   * Get all knowledge base items
   */
  async getKnowledgeItems(): Promise<KnowledgeItem[]> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch knowledge items');
    }

    return response.json();
  }

  /**
   * Upload a document to the knowledge base
   */
  async uploadDocument(file: File, category: string = 'general', priority: number = 0): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('category', category);
    formData.append('priority', priority.toString());

    const token = authService.getToken();
    const response = await fetch(`${API_URL}/api/knowledge/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload document');
    }

    return response.json();
  }

  /**
   * Delete a knowledge item
   */
  async deleteKnowledgeItem(id: number): Promise<{ success: boolean; message: string; deletedChunks?: number }> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/${id}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete knowledge item');
    }

    return response.json();
  }

  /**
   * Test search functionality
   */
  async testSearch(query: string, limit: number = 3, threshold: number = 0.7): Promise<SearchTestResponse> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/test-search`,
      {
        method: 'POST',
        body: JSON.stringify({ query, limit, threshold }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Search test failed');
    }

    return response.json();
  }

  /**
   * Get knowledge base statistics
   */
  async getStats(): Promise<KnowledgeStats> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/stats`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch statistics');
    }

    return response.json();
  }

  /**
   * Update a knowledge item
   */
  async updateKnowledgeItem(
    id: number,
    data: {
      category?: string;
      content?: string;
      question_answers?: any[];
      priority?: number;
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update knowledge item');
    }

    return response.json();
  }

  /**
   * Get text extractions for review
   */
  async getTextExtractions(status: string = 'all', limit: number = 50, offset: number = 0): Promise<{
    extractions: any[];
    total: number;
    hasMore: boolean;
  }> {
    const params = new URLSearchParams({
      status,
      limit: limit.toString(),
      offset: offset.toString()
    });

    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/extractions?${params}`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch text extractions');
    }

    return response.json();
  }

  /**
   * Update a text extraction after review
   */
  async updateTextExtraction(id: number, data: {
    reviewed_text: string;
    status: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/extractions/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update text extraction');
    }

    return response.json();
  }

  /**
   * Get text extraction statistics
   */
  async getExtractionStats(): Promise<{
    total: number;
    byStatus: Record<string, number>;
    averageConfidence: number;
    recentExtractions: number;
  }> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/extractions/stats`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch extraction statistics');
    }

    return response.json();
  }

  /**
   * Get knowledge chunks for browsing and management
   */
  async getKnowledgeChunks(params: {
    search?: string;
    document_id?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{
    chunks: any[];
    total: number;
    hasMore: boolean;
  }> {
    const searchParams = new URLSearchParams();
    
    if (params.search) searchParams.append('search', params.search);
    if (params.document_id) searchParams.append('document_id', params.document_id);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());

    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/chunks?${searchParams}`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch knowledge chunks');
    }

    return response.json();
  }

  /**
   * Get a single knowledge chunk with full content
   */
  async getKnowledgeChunk(id: number): Promise<any> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/chunks/${id}`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch knowledge chunk');
    }

    return response.json();
  }

  /**
   * Update a knowledge chunk
   */
  async updateKnowledgeChunk(id: number, data: {
    chunk_text: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/chunks/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update knowledge chunk');
    }

    return response.json();
  }

  /**
   * Delete a knowledge chunk
   */
  async deleteKnowledgeChunk(id: number): Promise<{ success: boolean; message: string }> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/chunks/${id}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete knowledge chunk');
    }

    return response.json();
  }

  /**
   * Create a new knowledge chunk
   */
  async createKnowledgeChunk(data: {
    chunk_text: string;
    knowledge_base_id?: number;
    category?: string;
  }): Promise<{ 
    success: boolean; 
    message: string;
    chunkId: number;
    knowledgeBaseId: number;
  }> {
    const response = await authService.makeAuthenticatedRequest(
      `${API_URL}/api/knowledge/chunks`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create knowledge chunk');
    }

    return response.json();
  }
}

export const knowledgeService = new KnowledgeService();
export default knowledgeService;